import { Button, Container, Icon, Stack, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { ReactElement, useCallback, useMemo, useState } from 'react';

import { PaginatedDataGrid } from 'app/components/paginated-data-grid';
import { ListOptions } from 'app/model/list-options';
import { Version } from 'app/model/machine-learning/version';
import { versionsService } from 'app/services/machine-learning/versions.service';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

export const MlVersions: () => ReactElement = () => {
    const [version, setVersion] = useState(0);
    const [disableIncrementing, setDisableIncrementing] = useState(false);
    const [disableExport, setDisableExport] = useState(false);
    const exportHandler = useCallback((versionForExport: Version) => {
        setDisableExport(true);
        versionsService.export(versionForExport).subscribe(() => {
            setDisableExport(false);
        });
    }, []);

    const resultColumns = useMemo(() => {
        const columns: Array<GridColDef<Version>> = [
            {
                field: 'id',
                headerName: 'ID',
                width: 150,
                flex: 1,
            },
            {
                field: 'version',
                headerName: 'Version',
                width: 150,
                flex: 1,
            },
            {
                field: 'isCurrent',
                headerName: 'Is Current',
                width: 150,
                flex: 1,
            },
            {
                field: 'userCreated',
                headerName: 'Created By',
                width: 150,
                flex: 1,
                valueGetter: DataGridFormatters.usernameFormatter,
            },
            {
                field: 'dateCreated',
                headerName: 'Date Created',
                width: 150,
                type: 'date',
                flex: 1,
                valueFormatter: DataGridFormatters.dateFormatter,
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: 'actions',
                width: 90,
                flex: 1,
                getActions: (params: GridRowParams<Version>) => [
                    <Tooltip title='Export'>
                        <GridActionsCellItem
                            disabled={disableExport}
                            label='Export'
                            icon={<Icon>drive_file_move</Icon>}
                            onClick={() => {
                                exportHandler(params.row);
                            }}
                        />
                    </Tooltip>,
                ],
            },
        ];

        return columns;
    }, [disableExport, exportHandler]);
    const searchHandler = useCallback((options?: ListOptions) => {
        return versionsService.list(options);
    }, []);

    const incrementVersionHandler = useCallback(() => {
        setDisableIncrementing(true);
        versionsService.increment().subscribe(() => {
            setVersion((oldVersion) => oldVersion + 1);
            setDisableIncrementing(false);
        });
    }, []);

    return (
        <Container sx={{ minHeight: '30rem', height: '100%', py: 3 }}>
            <Stack spacing={2} justifyContent='flex-start' alignItems={'flex-start'} sx={{ height: '100%' }}>
                <Typography variant='body1' className='mb-2'>
                    Here is the list of all the ML models that are in the system. Click increase version to create a new
                    one. Click the "Export" action to export the model to S3.
                </Typography>
                <Button
                    variant='contained'
                    sx={{ textTransform: 'none' }}
                    onClick={incrementVersionHandler}
                    disabled={disableIncrementing}
                >
                    Increase Version
                </Button>

                <PaginatedDataGrid
                    dataSide='server'
                    autoHeight
                    density='compact'
                    columns={resultColumns}
                    onSearch={searchHandler}
                    searchProps={{ version }}
                    initialState={{ sorting: { sortModel: [{ field: 'version', sort: 'desc' }] } }}
                />
            </Stack>
        </Container>
    );
};
